import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
// Shared lean landing component for ZH/JA/KO marriage hubs.
import type { MarriageI18nLocale } from "@/data/marriage-i18n";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { Link } from "@tanstack/react-router";
import { SEO_SITE } from "@/lib/seo";
import LocaleServiceLinks from "@/components/LocaleServiceLinks";
import { landingBlockLabels } from "@/data/landing-block-i18n";

export function MultilingualMarriageLanding({ l }: { l: MarriageI18nLocale }) {
  const sourceUrl = `${SEO_SITE}${l.path}`;
  const langCode = l.code;
  const t = landingBlockLabels(langCode);
  return (
    <main className="min-h-screen bg-white" lang={langCode}>
      <section className="bg-navy-deep px-4 py-14 text-white">
        <div className="mx-auto max-w-5xl">
          <p className="text-xs uppercase tracking-widest text-amber-300">{t.eyebrow}</p>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">{l.h1}</h1>
          <p className="mt-4 max-w-2xl text-white/85">{l.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="https://wa.me/66922239958"
              className="rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-navy-deep hover:bg-amber-300"
            >
              {l.ctaPrimary}
            </a>
            <Link
              to="/marriage"
              className="rounded-full border border-white/30 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10"
            >
              {l.ctaSecondary}
            </Link>
          </div>
          <p className="mt-3 text-xs text-white/60">{l.contactHint}</p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <AIAnswerBox
          question={l.quickAnswer.q}
          answer={l.quickAnswer.a}
          stats={l.stats}
          sourceUrl={sourceUrl}
          lang={langCode}
        />
      </div>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">
          {t.stepsTitle}
        </h2>
        <ol className="mt-5 space-y-4">
          {l.steps.map((s, i) => (
            <li key={s.name} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400 text-sm font-bold text-navy-deep">
                  {i + 1}
                </span>
                <div>
                  <h3 className="text-base font-bold text-navy-deep">{s.name}</h3>
                  <p className="mt-1 text-sm text-slate-600">{s.text}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="bg-slate-50 px-4 py-12 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep">{t.faqTitle}</h2>
          <div className="mt-5 space-y-3">
            {l.faqs.map((f) => (
              <details key={f.q} className="rounded-xl border border-slate-200 bg-white p-4">
                <summary className="cursor-pointer text-sm font-bold text-navy-deep">{f.q}</summary>
                <p className="mt-2 text-sm text-slate-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-700">
          <p className="font-semibold text-navy-deep">{l.contactHint}</p>
          <p className="mt-2">
            <a className="font-bold text-amber-700 hover:underline" href="https://wa.me/66922239958">
              WhatsApp +66 92 223 9958
            </a>
            {" · "}
            <a className="font-bold text-amber-700 hover:underline" href="mailto:info@nyclanguageinstitute.com">
              info@nyclanguageinstitute.com
            </a>
          </p>
        </div>
      </section>
          <ServicePageEnhancer serviceName="Marriage in Thailand" locale={l.code as never} />
      <LocaleServiceLinks />
</main>
  );
}
